import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import {
  CalendarIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  rsvpToEvent,
  getEventRsvps,
  type CalendarEvent,
  type CreateEventRequest,
  type EventType,
  type TargetAudience
} from '@/api/communicationApi'
import { CalendarView, AudienceSelector } from '@/components/communication'
import { Button } from '@/components/ui/Button'
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns'

const eventSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  isAllDay: z.boolean(),
  eventType: z.enum(['academic', 'sports', 'cultural', 'holiday', 'exam', 'meeting', 'parent_teacher', 'other']),
  requiresRsvp: z.boolean(),
  notifyOnCreate: z.boolean(),
  location: z.object({
    name: z.string().optional(),
    isVirtual: z.boolean(),
    meetingUrl: z.string().optional()
  })
})

type EventFormData = z.infer<typeof eventSchema>

const eventTypeConfig: Record<EventType, { label: string; color: string; bgColor: string }> = {
  academic: { label: 'Academic', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  sports: { label: 'Sports', color: 'text-green-700', bgColor: 'bg-green-100' },
  cultural: { label: 'Cultural', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  holiday: { label: 'Holiday', color: 'text-red-700', bgColor: 'bg-red-100' },
  exam: { label: 'Exam', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  meeting: { label: 'Meeting', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  parent_teacher: { label: 'Parent-Teacher', color: 'text-teal-700', bgColor: 'bg-teal-100' },
  other: { label: 'Other', color: 'text-gray-700', bgColor: 'bg-gray-100' }
}

export default function EventsPage() {
  const queryClient = useQueryClient()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null)
  const [viewingEvent, setViewingEvent] = useState<CalendarEvent | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [eventTypeFilter, setEventTypeFilter] = useState<EventType | ''>('')
  const [targetAudience, setTargetAudience] = useState<TargetAudience>({
    type: 'all_school'
  })

  // Calculate date range for API query
  const dateRange = useMemo(() => {
    const start = startOfMonth(subMonths(currentDate, 1))
    const end = endOfMonth(addMonths(currentDate, 1))
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    }
  }, [currentDate])

  // Fetch events
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', dateRange.startDate, dateRange.endDate, eventTypeFilter],
    queryFn: () => getEvents({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      eventType: eventTypeFilter || undefined
    })
  })

  // Fetch RSVPs for viewing event
  const { data: rsvpData } = useQuery({
    queryKey: ['event-rsvps', viewingEvent?.id],
    queryFn: () => getEventRsvps(viewingEvent!.id),
    enabled: !!viewingEvent
  })

  // Form
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      description: '',
      isAllDay: false,
      eventType: 'meeting',
      requiresRsvp: false,
      notifyOnCreate: true,
      location: {
        name: '',
        isVirtual: false,
        meetingUrl: ''
      }
    }
  })

  const isAllDay = watch('isAllDay')
  const isVirtual = watch('location.isVirtual')

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateEventRequest) => createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast.success('Event created successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create event')
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast.success('Event updated successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update event')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast.success('Event deleted successfully')
      setViewingEvent(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete event')
    }
  })

  const rsvpMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: 'attending' | 'maybe' | 'declined'; notes?: string }) =>
      rsvpToEvent(id, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['event-rsvps'] })
      toast.success('RSVP submitted')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to submit RSVP')
    }
  })

  const openCreateModal = (date?: Date) => {
    setEditingEvent(null)
    setTargetAudience({ type: 'all_school' })
    const dateStr = date ? format(date, "yyyy-MM-dd'T'HH:mm") : ''
    reset({
      title: '',
      description: '',
      startDate: dateStr,
      endDate: dateStr,
      isAllDay: false,
      eventType: 'meeting',
      requiresRsvp: false,
      notifyOnCreate: true,
      location: {
        name: '',
        isVirtual: false,
        meetingUrl: ''
      }
    })
    setIsModalOpen(true)
  }

  const openEditModal = (event: CalendarEvent) => {
    setEditingEvent(event)
    setTargetAudience(event.targetAudience || { type: 'all_school' })
    reset({
      title: event.title,
      description: event.description || '',
      startDate: format(parseISO(event.startDate), "yyyy-MM-dd'T'HH:mm"),
      endDate: format(parseISO(event.endDate), "yyyy-MM-dd'T'HH:mm"),
      isAllDay: event.isAllDay,
      eventType: event.eventType,
      requiresRsvp: event.requiresRsvp,
      notifyOnCreate: event.notifyOnCreate,
      location: {
        name: event.location?.name || '',
        isVirtual: event.location?.isVirtual || false,
        meetingUrl: event.location?.meetingUrl || ''
      }
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingEvent(null)
    reset()
  }

  const onSubmit = async (data: EventFormData) => {
    const payload: CreateEventRequest = {
      title: data.title,
      description: data.description,
      startDate: new Date(data.startDate).toISOString(),
      endDate: new Date(data.endDate).toISOString(),
      isAllDay: data.isAllDay,
      eventType: data.eventType,
      requiresRsvp: data.requiresRsvp,
      notifyOnCreate: data.notifyOnCreate,
      location: {
        name: data.location.name || 'TBD',
        isVirtual: data.location.isVirtual,
        meetingUrl: data.location.meetingUrl
      },
      targetAudience
    }

    if (editingEvent) {
      await updateMutation.mutateAsync({ id: editingEvent.id, data: payload })
    } else {
      await createMutation.mutateAsync(payload)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleRsvp = async (status: 'attending' | 'maybe' | 'declined') => {
    if (viewingEvent) {
      await rsvpMutation.mutateAsync({ id: viewingEvent.id, status })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events Calendar</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage school events and schedules
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value as EventType | '')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value="">All Event Types</option>
            {Object.entries(eventTypeConfig).map(([type, config]) => (
              <option key={type} value={type}>{config.label}</option>
            ))}
          </select>
          <Button onClick={() => openCreateModal()}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <CalendarView
        events={events}
        onDateClick={(date) => {
          setSelectedDate(date)
        }}
        onEventClick={(event) => setViewingEvent(event)}
        onCreateEvent={openCreateModal}
        selectedDate={selectedDate}
        isLoading={isLoading}
      />

      {/* Upcoming Events Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Can add additional content here */}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Upcoming Events</h3>
          <div className="space-y-3">
            {events
              .filter(e => new Date(e.startDate) >= new Date())
              .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
              .slice(0, 5)
              .map((event) => {
                const config = eventTypeConfig[event.eventType]
                return (
                  <button
                    key={event.id}
                    onClick={() => setViewingEvent(event)}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-1 h-full rounded-full ${config.bgColor.replace('100', '500')}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {format(parseISO(event.startDate), event.isAllDay ? 'MMM d, yyyy' : 'MMM d, yyyy HH:mm')}
                        </p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
                          {config.label}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
            {events.filter(e => new Date(e.startDate) >= new Date()).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No upcoming events</p>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50"
                onClick={closeModal}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingEvent ? 'Edit Event' : 'Create Event'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Title
                    </label>
                    <input
                      {...register('title')}
                      type="text"
                      placeholder="Enter event title"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      placeholder="Add event description..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Event Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Type
                    </label>
                    <select
                      {...register('eventType')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(eventTypeConfig).map(([type, config]) => (
                        <option key={type} value={type}>{config.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* All Day Toggle */}
                  <div>
                    <Controller
                      name="isAllDay"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">All-day event</span>
                        </label>
                      )}
                    />
                  </div>

                  {/* Date/Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start {isAllDay ? 'Date' : 'Date & Time'}
                      </label>
                      <input
                        {...register('startDate')}
                        type={isAllDay ? 'date' : 'datetime-local'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {errors.startDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End {isAllDay ? 'Date' : 'Date & Time'}
                      </label>
                      <input
                        {...register('endDate')}
                        type={isAllDay ? 'date' : 'datetime-local'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {errors.endDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <Controller
                      name="location.isVirtual"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Virtual event (online)</span>
                        </label>
                      )}
                    />
                    {isVirtual ? (
                      <input
                        {...register('location.meetingUrl')}
                        type="url"
                        placeholder="Meeting URL (e.g., https://meet.google.com/...)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    ) : (
                      <input
                        {...register('location.name')}
                        type="text"
                        placeholder="Location name (e.g., Main Hall, Room 101)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    )}
                  </div>

                  {/* Target Audience */}
                  <AudienceSelector
                    value={targetAudience}
                    onChange={setTargetAudience}
                    showPreview={true}
                  />

                  {/* Options */}
                  <div className="space-y-3">
                    <Controller
                      name="requiresRsvp"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Require RSVP</span>
                        </label>
                      )}
                    />
                    <Controller
                      name="notifyOnCreate"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Send notification when created</span>
                        </label>
                      )}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button type="button" variant="secondary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
                    >
                      {editingEvent ? 'Update Event' : 'Create Event'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* View Event Modal */}
      <AnimatePresence>
        {viewingEvent && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50"
                onClick={() => setViewingEvent(null)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-xl shadow-xl w-full max-w-lg"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Event Details</h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(viewingEvent)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(viewingEvent.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewingEvent(null)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{viewingEvent.title}</h3>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${eventTypeConfig[viewingEvent.eventType].bgColor} ${eventTypeConfig[viewingEvent.eventType].color}`}>
                      {eventTypeConfig[viewingEvent.eventType].label}
                    </span>
                  </div>

                  {viewingEvent.description && (
                    <p className="text-gray-600">{viewingEvent.description}</p>
                  )}

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      {viewingEvent.isAllDay ? (
                        <span>
                          {format(parseISO(viewingEvent.startDate), 'MMMM d, yyyy')}
                          {viewingEvent.startDate !== viewingEvent.endDate && (
                            <> - {format(parseISO(viewingEvent.endDate), 'MMMM d, yyyy')}</>
                          )}
                        </span>
                      ) : (
                        <span>
                          {format(parseISO(viewingEvent.startDate), 'MMMM d, yyyy HH:mm')} -{' '}
                          {format(parseISO(viewingEvent.endDate), 'HH:mm')}
                        </span>
                      )}
                    </div>

                    {viewingEvent.location && (
                      <div className="flex items-center text-gray-600">
                        {viewingEvent.location.isVirtual ? (
                          <>
                            <VideoCameraIcon className="h-4 w-4 mr-2" />
                            <a
                              href={viewingEvent.location.meetingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline"
                            >
                              Join Meeting
                            </a>
                          </>
                        ) : (
                          <>
                            <MapPinIcon className="h-4 w-4 mr-2" />
                            <span>{viewingEvent.location.name}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RSVP Section */}
                  {viewingEvent.requiresRsvp && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">RSVP</h4>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleRsvp('attending')}
                          loading={rsvpMutation.isPending}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Attending
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRsvp('maybe')}
                          loading={rsvpMutation.isPending}
                        >
                          <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
                          Maybe
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRsvp('declined')}
                          loading={rsvpMutation.isPending}
                        >
                          Decline
                        </Button>
                      </div>

                      {rsvpData && (
                        <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center">
                            <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                            {rsvpData.summary.attending} attending
                          </span>
                          <span className="flex items-center">
                            <QuestionMarkCircleIcon className="h-3 w-3 mr-1 text-yellow-500" />
                            {rsvpData.summary.maybe} maybe
                          </span>
                          <span>{rsvpData.summary.declined} declined</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-400 pt-2 border-t border-gray-200">
                    <p>Created: {format(parseISO(viewingEvent.createdAt), 'PPpp')}</p>
                    {viewingEvent.createdBy && (
                      <p>By: {viewingEvent.createdBy.firstName} {viewingEvent.createdBy.lastName}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
